import classNames from 'classnames';
import { useMemo } from 'react';

import Error404 from 'components/navigation/Error404';
import { IFile } from 'features/files/fileAPI';
import {
  getFileTypeInfo,
  isImageType,
  isVideoType,
} from 'features/files/fileSlice';
import { IFolder } from 'features/folders/folderAPI';
import { END_POINT } from 'helpers/consts';
import { useSite } from 'selectors/siteSelectors';

type LibraryPathViewProps = {
  pathSegments: string[];
};

function normalizePathSegment(segment: string): string {
  return decodeURIComponent(segment).trim();
}

export function getLibraryPathForFile(file: IFile): string {
  const pathSegments: string[] = [];
  const fileName = file.fileName?.trim();
  if (fileName) {
    pathSegments.unshift(fileName);
  }
  let currentFolder =
    file.folder && typeof file.folder === 'object' ? file.folder : null;
  while (currentFolder) {
    const folderName = currentFolder.folderName?.trim();
    if (folderName && folderName !== '.') {
      pathSegments.unshift(folderName);
    }
    const parentFolder = currentFolder.parentFolder;
    currentFolder =
      parentFolder && typeof parentFolder === 'object' ? parentFolder : null;
  }
  const path = `/Library/${pathSegments.map(encodeURIComponent).join('/')}`;
  return END_POINT ? new URL(path, END_POINT).origin + path : path;
}

function findFileByPath(
  folder: IFolder | null,
  pathSegments: string[],
): IFile | null {
  if (!folder || pathSegments.length === 0) return null;
  const [currentSegment, ...remainingSegments] = pathSegments;
  const normalizedSegment = normalizePathSegment(currentSegment);
  if (remainingSegments.length === 0) {
    return (
      folder.files?.find(
        (file) =>
          normalizePathSegment(file.fileName ?? '') === normalizedSegment,
      ) ?? null
    );
  }
  const nextFolder = folder.subFolders?.find(
    (subFolder) =>
      normalizePathSegment(subFolder.folderName ?? '') === normalizedSegment,
  );
  return findFileByPath(nextFolder ?? null, remainingSegments);
}

export default function LibraryFileViewer(props: LibraryPathViewProps) {
  const { pathSegments } = props;
  const { library } = useSite();
  const file = useMemo(
    () => findFileByPath(library?.rootFolder ?? null, pathSegments),
    [library?.rootFolder, pathSegments.join('/')],
  );

  if (!library || !library.rootFolder) return null;
  if (!file) return <Error404 />;
  return <LibraryFilePreview file={file} />;
}

type LibraryFilePreviewProps = {
  file: IFile;
};

function LibraryFilePreview(props: LibraryFilePreviewProps) {
  const { file } = props;
  const fileType = getFileTypeInfo(file);
  const viewSource = getLibraryPathForFile(file);
  const title = file.description || file.fileName || 'Library file';

  return (
    <div className={classNames('static_page', 'library', 'library_file_view')}>
      <div className="page_container">
        <div className="page_content library_file_view_content">
          <section className="page_header">
            <h1>{title}</h1>
            {file.fileName ? (
              <div className="static_text">{file.fileName}</div>
            ) : null}
          </section>
          {viewSource ? (
            isImageType(fileType.mime) ? (
              <img src={viewSource} alt={title} />
            ) : isVideoType(fileType.mime) ? (
              <video controls preload="auto" width="100%" height="auto">
                <source
                  src={viewSource}
                  type={file.mimeType ?? fileType.mime}
                />
              </video>
            ) : (
              <iframe
                src={viewSource}
                title={title}
                style={{ width: '100%', height: '80vh', border: 0 }}
              />
            )
          ) : (
            <div>Loading file...</div>
          )}
        </div>
      </div>
    </div>
  );
}
